import React from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components/macro';
import { useDrag } from 'react-dnd';
import { dndConstants } from 'store/constants/dnd.constants';
import {
  ApiAttachmentForFS,
  EachThumbnailWithPdfUrl,
} from 'store/models/MainFiles';
import {
  commonPagesActions,
  fileSelectedActions,
  previewFileActions,
} from 'store/actions';
import {
  NavigateNextRounded,
  SearchRounded,
} from '@material-ui/icons';
import { Fab } from '@material-ui/core';

export const THUMBNAIL_HEIGHT = '133px';
export const THUMBNAIL_WIDTH = '105px';
// export const THUMBNAIL_HEIGHT = '10vw';
// export const THUMBNAIL_WIDTH = '7vw';

interface SrcThumbnailProps {
  thumbnail: EachThumbnailWithPdfUrl;
  srcFile: ApiAttachmentForFS;
  hasAnyAddableFileSelected: boolean;
  index?: number;
  allUrls?: string[];
}

const handleShowPreview = (
  dispatch,
  currentIndex?: number,
  allUrls?: string[],
) => {
  if (allUrls && currentIndex !== undefined) {
    const allPages = allUrls.map((url) => ({
      url,
      mimeType: 'application/pdf',
    }));
    dispatch(
      previewFileActions.showPdfWithPagination(
        currentIndex,
        allPages,
      ),
    );
  } else {
    throw Error(
      `invalid index or allUrls ${currentIndex} ${allUrls}`,
    );
  }
};

/**
 * srcFile is passed if is SrcThumbnail. If Dest, no srcFile passed
 * @param dispatch
 * @param thumbnail
 * @param srcFile
 */
export const renderPreviewCover = (
  dispatch,
  thumbnail,
  srcFile?,
  hasAnyAddableFileSelected?,
) => {
  return (
    <SPreview>
      <SPreviewTexts>
        {srcFile && (
          <SEachPreviewText
            onClick={(e) => {
              dispatch(
                commonPagesActions.addFileOrPage(
                  srcFile,
                  thumbnail.num,
                ),
              );
            }}
          >
            <SText>Common</SText>

            <SIcon>
              <NavigateNextRounded fontSize="small" />
            </SIcon>
          </SEachPreviewText>
        )}
        {/* it has to not be undefined or false */}
        {hasAnyAddableFileSelected === true && srcFile && (
          <SEachPreviewText
            onClick={(e) => {
              dispatch(
                fileSelectedActions.appendPageToSelectedAttIndexes(
                  srcFile,
                  thumbnail.num,
                ),
              );
            }}
          >
            <SText>Selected Files</SText>

            <SIcon>
              <NavigateNextRounded fontSize="small" />
            </SIcon>
          </SEachPreviewText>
        )}
        {srcFile && (
          <SEachPreviewText
            onClick={(e) => {
              dispatch(
                fileSelectedActions.appendSrcFile(
                  srcFile,
                  false,
                  thumbnail.num,
                ),
              );
            }}
          >
            <SText>New File</SText>

            <SIcon>
              <NavigateNextRounded fontSize="small" />
            </SIcon>
          </SEachPreviewText>
        )}
      </SPreviewTexts>
    </SPreview>
  );
};

export const renderPreviewFab = (dispatch, index?, allUrls?) => {
  return (
    <SPreviewFab
      size="small"
      onClick={(e) => {
        console.debug(`click`);
        handleShowPreview(dispatch, index, allUrls);
      }}
    >
      <SearchRounded />
    </SPreviewFab>
  );
};

const SrcThumbnail: React.FC<SrcThumbnailProps> = ({
  srcFile,
  thumbnail,
  hasAnyAddableFileSelected,
  index,
  allUrls,
}) => {
  const dispatch = useDispatch();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [{ isDragging }, drag] = useDrag({
    item: {
      type: dndConstants.SRC_PAGE,
      payload: { srcFile: srcFile, pageNum: thumbnail.num },
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  return (
    <SThumbnailAndPage>
      <SThumbnail
        key={thumbnail.filename}
        className={`moveable ${
          thumbnail.isInFileSelected ? 'in-fileselected' : ''
        } ${
          thumbnail.isInOtherFileSelected
            ? 'in-other-fileselected'
            : ''
        }
        `}
        ref={drag}
      >
        {renderPreviewCover(
          dispatch,
          thumbnail,
          srcFile,
          hasAnyAddableFileSelected,
        )}
        <img src={thumbnail.url} alt={thumbnail.filename} />
        {renderPreviewFab(dispatch, index, allUrls)}
      </SThumbnail>
      <SPage>{`${thumbnail.num}/${srcFile.thumbnails.length}`}</SPage>
    </SThumbnailAndPage>
  );
};

export default SrcThumbnail;

export const SThumbnailAndPage = styled.div``;
const SText = styled.div.attrs((props) => ({
  className: props.className,
}))`
  font-size: ${(props) => props.theme.typography.fontSize.extraSmall};
  text-align: center;
  padding-right: 1rem;
  &.dest {
    font-size: ${(props) => props.theme.typography.fontSize.small};
    padding-right: 0;
  }
`;

export const SPreview = styled.div`
  width: 100%;
  height: 100%;
  color: ${(props) => props.theme.palette.common.white};
  background: rgba(0, 0, 0, 0.6);
  position: absolute;
  display: none;
  justify-content: center;
  align-items: center;

  /* why do we have this? @sarun */
  /* &:active {
    background: none;
    & > div,
    svg {
      display: none;
    }
  } */
`;
// all width height box-shawdow are copied from figmas
const SPreviewFab = styled(Fab)`
  &.MuiFab-root {
    position: absolute;
    right: -1rem;
    bottom: -1.2rem;
    display: none;
    z-index: 1;
  }
`;

export const SThumbnail = styled.div`
  width: ${THUMBNAIL_WIDTH};
  height: ${THUMBNAIL_HEIGHT};
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  position: relative;
  &:hover > ${SPreview} {
    display: flex;
  }
  &:hover > ${SPreviewFab}.MuiFab-root {
    display: flex;
  }
  &.in-fileselected {
    border: 3px solid #02d57c;
  }
  &.in-other-fileselected:not(:hover)::after {
    content: '';
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    position: absolute;
    top: 0;
    left: 0;
    box-sizing: border-box;
  }
  &.from-global {
    border: 3px solid ${(props) => props.theme.palette.primary.main};
  }
`;
export const SPage = styled.div`
  font-size: ${(props) => props.theme.typography.fontSize.body};
  text-align: center;
  margin-top: 0.3rem;
`;
const SPreviewTexts = styled.div`
  width: 93%;
`;
const SEachPreviewText = styled.div`
  cursor: pointer;
  text-align: center;
  position: relative;
  align-items: center;
  & + & {
    margin-top: 0.5rem;
  }
  gap: 2px;
  border-radius: 1rem;
  border: 1px solid white;
  justify-content: center;
  line-height: 2;
  background: transparent;
  transition: background 0.15s;
  &:hover {
    background: #b73f8361;
  }
`;
const SIcon = styled.div`
  position: absolute;
  right: 2px;
  top: 0px;
`;
